import { useId, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import ColorBox from "devextreme-react/color-box";
import {
    constructionSystems,
    representationForDefinition,
    type ConstructionSystem,
    type MeasureForm,
} from "@treegram/viewer-core";
import measureShapes from "../measure-shapes";
import Button from "@/components/buttons/button";
import Select from "@/components/select";
import MeasureSection from "../measure-section";
import MeasureTypeFields from "./measure-type-fields";

interface Props {
    disabled?: boolean;
    name?: string;
    shape: MeasureForm;
    system: ConstructionSystem;
    values?: Record<string, unknown>;
    color?: string;
    availableShapes?: string[];
    representationOnly?: boolean;
    onShapeChange?: (shape: MeasureForm) => void;
    onSystemChange: (system: ConstructionSystem) => void;
}

export default function MeasureTypeEditor({
    disabled = false,
    name = "",
    shape,
    system,
    values = {},
    color = "#3B82F6FF",
    availableShapes,
    representationOnly = false,
    onShapeChange,
    onSystemChange,
}: Props) {
    const id = useId();
    const [currentColor, setCurrentColor] = useState(color);
    const [businessTouched, setBusinessTouched] = useState(false);
    const [draftDimensions, setDraftDimensions] = useState<Record<string, string>>({});
    const changeSystem = (next: ConstructionSystem) => {
        setBusinessTouched(true);
        setDraftDimensions({});
        onSystemChange(next);
    };
    const options = Object.entries(constructionSystems)
        .filter(([key, entry]) => key !== "none" && entry.forms.includes(shape))
        .map(([value, entry]) => ({
            value: value as ConstructionSystem,
            label: entry.label,
        }));
    const representationId = representationForDefinition(shape, system).id;
    const enabled = system !== "none";
    const dimensionsSummary = constructionSystems[system].attributes
        .flatMap((key) => {
            const labels: Record<string, string> = {
                height: "H",
                width: "L",
                thickness: "Ép.",
                offset: "Allège",
            };
            const initialValue =
                typeof values[key] === "number" ? String(Number((Number(values[key]) * 100).toFixed(6))) : "";
            const value = draftDimensions[key] ?? initialValue;
            return value && labels[key] ? [`${labels[key]} ${value} cm`] : [];
        })
        .join(" · ");
    const opacity =
        currentColor.startsWith("#") && currentColor.length === 9
            ? parseInt(currentColor.slice(7), 16) / 255
            : Number(currentColor.match(/rgba\([^,]+,[^,]+,[^,]+,\s*([\d.]+)\)/)?.[1] ?? 1);

    return (
        <div className="min-h-0 overflow-y-auto px-1 text-sm">
            <label htmlFor={`${id}-name`} className="mb-4 block">
                <span className="mb-1.5 block font-medium">Nom du type</span>
                <input
                    id={`${id}-name`}
                    name="name"
                    required
                    autoFocus={!representationOnly}
                    defaultValue={name}
                    className="w-full rounded-md border border-gray-300 bg-white px-2 py-2 text-sm focus:border-indigo-600 focus:ring-indigo-600"
                />
            </label>

            <MeasureSection
                title="Représentation"
                defaultOpen
                summary={
                    <span className="inline-flex items-center gap-2">
                        {measureShapes.find((item) => item.value === shape)?.label}
                        <span
                            className="inline-block size-4 rounded-sm border border-gray-300"
                            style={{ backgroundColor: currentColor }}
                        />
                        {Math.round(opacity * 100)} %
                    </span>
                }
            >
                <fieldset className="mb-4">
                    <legend className="mb-2">Forme de saisie</legend>
                    <input type="hidden" name="shape" value={shape} />
                    <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                        {measureShapes
                            .filter((item) => !availableShapes || availableShapes.includes(item.value))
                            .map((item) => (
                                <Button
                                    key={item.value}
                                    variant="ghost"
                                    size="md"
                                    aria-pressed={shape === item.value}
                                    disabled={disabled || (!onShapeChange && shape !== item.value)}
                                    onClick={() => onShapeChange?.(item.value)}
                                    className={`justify-center border px-2 ${shape === item.value ? "border-indigo-600 bg-indigo-50 text-indigo-600 hover:bg-indigo-50" : "border-gray-200"}`}
                                >
                                    <FontAwesomeIcon icon={item.icon} />
                                    {item.label}
                                </Button>
                            ))}
                    </div>
                    {!onShapeChange && (
                        <p className="mt-2 text-xs text-gray-500">
                            La forme de saisie est conservée pour les métrés existants.
                        </p>
                    )}
                </fieldset>
                <label htmlFor={`${id}-color`} className="mb-1.5 block">
                    Couleur et opacité
                </label>
                <ColorBox
                    disabled={disabled}
                    name="color"
                    value={currentColor}
                    inputAttr={{ id: `${id}-color` }}
                    onValueChanged={(event) => {
                        if (event.value) setCurrentColor(event.value);
                    }}
                    editAlphaChannel
                    applyButtonText="Appliquer"
                    cancelButtonText="Annuler"
                    openOnFieldClick
                />
                <div className="mt-4">
                    <MeasureTypeFields
                        key={shape}
                        representationId={representationId}
                        values={values}
                        role="representation"
                        disabled={disabled}
                    />
                </div>
            </MeasureSection>

            <MeasureSection
                key={`${shape}:${enabled}:${representationOnly}`}
                title="Caractéristiques métier"
                defaultOpen={enabled && (!representationOnly || businessTouched)}
                summary={
                    enabled
                        ? [constructionSystems[system].label, dimensionsSummary].filter(Boolean).join(" · ")
                        : "Sans système constructif"
                }
            >
                <label className="flex items-center gap-2">
                    <input
                        type="checkbox"
                        className="rounded accent-indigo-600"
                        checked={enabled}
                        disabled={disabled || !options.length}
                        onChange={(event) => changeSystem(event.target.checked ? options[0].value : "none")}
                    />
                    Activer un système constructif
                </label>
                <input type="hidden" name="constructionSystem" value={system} />
                {enabled ? (
                    <div
                        className="mt-4 flex flex-col gap-4"
                        onChange={(event) => {
                            const { target } = event;
                            if (
                                target instanceof HTMLInputElement &&
                                target.name.startsWith("dataFormatType_")
                            ) {
                                const key = target.name.replace("dataFormatType_", "");
                                setDraftDimensions((previous) => ({
                                    ...previous,
                                    [key]: target.value,
                                }));
                            }
                        }}
                    >
                        <div>
                            <label htmlFor={`${id}-system`} className="mb-1.5 block">
                                Système constructif
                            </label>
                            <Select
                                isDisabled={disabled}
                                inputId={`${id}-system`}
                                options={options}
                                value={options.find((option) => option.value === system)}
                                onChange={(option) => {
                                    if (option) changeSystem(option.value);
                                }}
                            />
                        </div>
                        <MeasureTypeFields
                            key={`${shape}:${system}`}
                            representationId={representationId}
                            values={values}
                            role="business"
                            disabled={disabled}
                        />
                    </div>
                ) : (
                    <p className="mt-3 rounded-md bg-gray-50 p-3 text-xs text-gray-500">
                        Sans système constructif. Le métré conserve ses propriétés géométriques et graphiques.
                    </p>
                )}
            </MeasureSection>
        </div>
    );
}
